import { useMemo, useState } from "react";
import { createBusinessLead, importBusinessLeads } from "../../../api/leads.service";

type Props = {
  businessPhone: string;
  instagramBusinessId: string;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
};

type CsvTable = {
  headers: string[];
  rows: string[][];
};

function splitLine(line: string, separator: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === separator && !quoted) {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell.trim());
  return cells;
}

function parseCsv(text: string): CsvTable {
  const source = text.replace(/^\uFEFF/, "").trim();
  const headerLine = source.split(/\r?\n/, 1)[0] || "";
  const separator = (headerLine.match(/;/g) || []).length > (headerLine.match(/,/g) || []).length ? ";" : ",";
  const lines = source.split(/\r?\n/).map((line) => splitLine(line, separator)).filter((row) => row.some(Boolean));
  const opening = lines[0] || [];
  const headerless = (opening[1] || "").replace(/\D/g, "").length >= 8;
  const headers = headerless
    ? opening.map((_, index) => `Column ${index + 1}`)
    : opening.map((header, index) => header || `Column ${index + 1}`);
  return { headers, rows: headerless ? lines : lines.slice(1) };
}

function guessColumn(headers: string[], pattern: RegExp): string {
  const index = headers.findIndex((header) => pattern.test(header));
  return index >= 0 ? String(index) : "";
}

export default function CreateLeadDialog({ businessPhone, instagramBusinessId, onClose, onCreated }: Props) {
  const [mode, setMode] = useState<"one" | "csv">("one");
  const [name, setName] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [instagramId, setInstagramId] = useState("");
  const [table, setTable] = useState<CsvTable | null>(null);
  const [nameCol, setNameCol] = useState("");
  const [whatsappCol, setWhatsappCol] = useState("");
  const [instagramCol, setInstagramCol] = useState("");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");

  const mapped = useMemo(() => {
    if (!table || nameCol === "" || whatsappCol === "") return [];
    return table.rows.map((row) => ({
      name: (row[Number(nameCol)] || "").trim(),
      whatsappNumber: (row[Number(whatsappCol)] || "").trim(),
      instagramId: instagramCol === "" ? "" : (row[Number(instagramCol)] || "").trim(),
    })).filter((row) => row.name && row.whatsappNumber);
  }, [table, nameCol, whatsappCol, instagramCol]);

  const readFile = async (file: File) => {
    const parsed = parseCsv(await file.text());
    if (parsed.headers.length === 0 || parsed.rows.length === 0) {
      setError("This file has no rows to import.");
      setTable(null);
      return;
    }
    const guessedName = guessColumn(parsed.headers, /name|contact|person/i);
    const guessedWhatsapp = guessColumn(parsed.headers, /whats|phone|mobile|number|\bwa\b/i);
    setNameCol(guessedName || (parsed.headers.length >= 1 ? "0" : ""));
    setWhatsappCol(guessedWhatsapp && guessedWhatsapp !== guessedName ? guessedWhatsapp : (parsed.headers.length >= 2 ? "1" : ""));
    setInstagramCol(guessColumn(parsed.headers, /insta|\big\b/i));
    setTable(parsed);
    setError("");
  };

  const saveOne = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Type a name.");
      return;
    }
    if (!businessPhone) {
      setError("This business has no WhatsApp number yet.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await createBusinessLead({
        businessId: businessPhone,
        instagramBusinessId,
        name: trimmed,
        whatsappNumber,
        instagramId,
      });
      if (!result?.success || !result.conversationId) {
        setError(result?.error || "Could not create the lead.");
        return;
      }
      onCreated(result.conversationId);
    } catch {
      setError("Could not create the lead.");
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async () => {
    if (!businessPhone) {
      setError("This business has no WhatsApp number yet.");
      return;
    }
    if (!nameCol || !whatsappCol) {
      setError("Map the name column and the WhatsApp number column.");
      return;
    }
    if (mapped.length === 0) {
      setError("No rows have both a name and a WhatsApp number.");
      return;
    }
    setSaving(true);
    setError("");
    setProgress("Saving...");
    try {
      const result = await importBusinessLeads({
        businessId: businessPhone,
        instagramBusinessId,
        leads: mapped,
      });
      if (!result?.success || !result.conversationId) {
        setError(result?.error || "Could not import these leads.");
        return;
      }
      onCreated(result.conversationId);
    } catch {
      setError("Could not import these leads.");
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-lg" onClick={(event) => event.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900">Create lead</h3>
        <div className="mt-3 flex gap-1">
          {(["one", "csv"] as const).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => { setMode(id); setError(""); }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${mode === id ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}
            >
              {id === "one" ? "One person" : "Upload CSV"}
            </button>
          ))}
        </div>

        {mode === "one" ? (
          <>
            <p className="mt-3 text-sm text-gray-500">This person is saved for this business. Their messages open after you select them.</p>
            <label className="mt-4 block text-sm text-gray-700">
              Name
              <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </label>
            <label className="mt-3 block text-sm text-gray-700">
              WhatsApp number
              <input value={whatsappNumber} onChange={(event) => setWhatsappNumber(event.target.value)} placeholder="Optional" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </label>
            <label className="mt-3 block text-sm text-gray-700">
              Instagram
              <input value={instagramId} onChange={(event) => setInstagramId(event.target.value)} placeholder="Optional" className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </label>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm text-gray-500">Upload a file, then match each column. Name and WhatsApp number are required. Instagram is only needed when the file has it.</p>
            <label className="mt-4 block text-sm text-gray-700">
              CSV file
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) readFile(file);
                }}
                className="mt-1 block w-full text-sm text-gray-600"
              />
            </label>
            {table ? (
              <div className="mt-4 space-y-3">
                <ColumnMap label="Name" headers={table.headers} value={nameCol} onChange={setNameCol} />
                <ColumnMap label="WhatsApp number" headers={table.headers} value={whatsappCol} onChange={setWhatsappCol} />
                <ColumnMap label="Instagram" headers={table.headers} value={instagramCol} onChange={setInstagramCol} optional />
                <p className="text-xs text-gray-500">
                  {mapped.length} of {table.rows.length} rows have a name and a WhatsApp number.
                </p>
                <div className="max-h-40 overflow-auto rounded-lg border border-gray-100">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-2 py-1.5 font-medium">Name</th>
                        <th className="px-2 py-1.5 font-medium">WhatsApp</th>
                        <th className="px-2 py-1.5 font-medium">Instagram</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mapped.slice(0, 5).map((row, index) => (
                        <tr key={`${row.whatsappNumber}-${index}`} className="border-t border-gray-100">
                          <td className="px-2 py-1.5">{row.name}</td>
                          <td className="px-2 py-1.5">{row.whatsappNumber}</td>
                          <td className="px-2 py-1.5">{row.instagramId || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}

        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-600">Cancel</button>
          <button
            type="button"
            onClick={mode === "one" ? saveOne : importCsv}
            disabled={saving || (mode === "csv" && mapped.length === 0)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {saving ? (progress || "Creating...") : mode === "one" ? "Create" : `Import ${mapped.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ColumnMap({
  label,
  headers,
  value,
  onChange,
  optional,
}: {
  label: string;
  headers: string[];
  value: string;
  onChange: (value: string) => void;
  optional?: boolean;
}) {
  return (
    <label className="block text-sm text-gray-700">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
        {optional ? <option value="">Not in this file</option> : <option value="">Choose a column</option>}
        {headers.map((header, index) => (
          <option key={`${header}-${index}`} value={String(index)}>{header}</option>
        ))}
      </select>
    </label>
  );
}
